import React, { useState, useRef, useEffect } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getMainColor } from '@panel/commons/lib/theme';
import { getColorByNumber, getBackgroundByNumber } from '@panel/commons/helpers/scoreHelpers';
import { useSelector } from 'react-redux';
import { CompanyPubrSelectors } from '@panel/usuarios/src/store/ducks/myCompany';
import { Icon, useOutsideClick, Button, ModalBase, sendCustomTrackingInfo } from '@panel/commons';
import { puerSendTrackingInfo } from '@panel/usuarios/src/services/utils/gtmUtils';
import { Modal } from '@panel/avisos/src/components/Modals/ModalWrapper';
import IntegratorModal from '@panel/avisos/src/components/Modals/IntegratorModal';
import { useThemeBreakpoint } from '@panel/leads/src/components/hooks/useResize';
import NotificationButton from '@panel/commons/lib/Notifications/NotificationButton';
import { theme } from '@navent-re/global-styled';
import ModalProviderMonorepo from '@panel/commons/lib/Notifications/modal/src/modalContext';
import { UserDropdown } from '../userDropdown/UserDropdown';
import ModalWithTimer from '../../../../../components/modal/ModalWithTimer';
import { RedirectMessage, useModal } from '../../../../../components/modal';
import { useContactOwner } from '../../../../../hooks/useContactOwner';
import { ContactOwner } from '../../../../../components/contactOwner';
import { usePublisherUnifiedPanel } from '../../../../../hooks/usePublisherUnifiedPanel';
import { bumexFeatureSelectors } from '../../../../../state/ducks/bumexFeatures';
import { reputationLevelSelectors } from '../../../../../state/ducks/reputationLevel';
import { bumexFeaturesKeys } from '../../../../../constants/bumexFeatures';
import { getLinkToPublish } from '../../../../../utils/getPublisherLinks';

// TODO : Review Theme colors applied on this and other components

const MenuRightContainer = styled.div`
  align-items: center;
  display: flex;
  line-height: 60px;
  position: relative;
  gap: 16px;
`;

const MenuUserWrapper = styled.div`
  display: flex;
  margin-left: -15px;
  margin-right: 0px;
  cursor: pointer;
  overflow: hidden;
  .userNameContainer {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .userName {
    font-size: 18px;
    font-weight: 700;
    position: absolute;
    top: 0px;
    right: 20px;
  }
`;

const AvatarImg = styled.svg.attrs({
  version: '1.1',
  xmlns: 'http://www.w3.org/2000/svg',
  xmlnsXlink: 'http://www.w3.org/1999/xlink'
})`
  height: 55px;
  width: 55px;

  .externalBorder {
    stroke-width: 1;
    stroke: #000000;
    fill: none; /* Asegura que el círculo interno no esté relleno */
  }

  .secondaryBorder {
    stroke-width: 6;
    stroke: ${props => props.borderColor || ''};
    fill: none;
  }

  ${({ active }) =>
    active &&
    css`
      .externalBorder {
        stroke: ${getMainColor()};
        fill: ${props => props.Secondary || ''};
      }

      .secondaryBorder {
        stroke: ${props => props.borderColor || ''};
      }
    `};

  ${({ activeBorderPrimary }) =>
    activeBorderPrimary === false &&
    css`
      .externalBorder {
        stroke: #000000;
      }
    `};
`;

const IconsContainer = styled.div`
  padding-left: 0px;
  display: flex;
  align-items: center;
  margin-top: 4px;
  .arrow-down {
    display: flex;
    align-items: center;
  }
`;

function HeaderMenuRight({ user, isPortalWithNewReputation }) {
  const { t } = useTranslation();
  const history = useHistory();
  const { publisherTypeId, isSeeker, isIntegrator, isPublisherMasked, publisherId, name, email } = user;
  const [showDropdown, setShowDropdown] = useState(false);
  const { modal } = useSelector(CompanyPubrSelectors.getCompanyPubr);
  const reputationLevelState = useSelector(reputationLevelSelectors.reputationLevelState);
  const siteId = process.env.REACT_APP_BUILD_SITE;
  const sitesWithoutEcommerce = ['PLEC', 'LLVE'];
  const canBuyPlans = sitesWithoutEcommerce.indexOf(siteId) === -1;
  const userIsParticular =
    (publisherTypeId === '1' || isSeeker) &&
    ['DVPE', 'UBPE', 'ZPAR', '24MX', 'IWBR', 'WIBR', 'PLEC', 'CAPA', 'CMBR', 'VAMX'].includes(siteId);
  const buyPlansUrl = userIsParticular ? t('panel.url.ecommerce.particular') : t('panel.url.ecommerce.professional');
  const userType = userIsParticular ? 'Particular' : 'Profesional';

  const [textBuy, setTextBuy] = useState();
  const [textPublish, setTextPublish] = useState();
  const [showModalIntegrator, setShowModalIntegrator] = useState(false);
  const [redirectPublisher, setRedirectPublisher] = useState(false);
  const [color, setColor] = useState(1);

  const { toggleModal, letToggleModal } = useModal();
  const [breakpoint] = useThemeBreakpoint();

  const withHeaderButtons = !history.location.pathname.includes('publicador');
  const { bumexFeatures } = useSelector(bumexFeatureSelectors.getBumexFeatureState);
  const { getAutologin } = usePublisherUnifiedPanel();

  const handleClickBuy = () => {
    puerSendTrackingInfo({
      event: 'trackEvent',
      eventCategory: 'Publicador',
      eventAction: 'Compra de planes',
      eventLabel: 'CTA Header Comprar'
    });
  };

  const handleClick = () => {
    if (isPublisherMasked) {
      letToggleModal(true);
      getAutologin({ modal: 'timer' });
    }
    if (isIntegrator) {
      setShowModalIntegrator(true);
    }
    sendCustomTrackingInfo({
      event: 'trackEvent',
      eventCategory: 'Publicador',
      eventAction: 'Iniciar Publicacion',
      eventLabel: 'CTA Header Publicar gratis - Panel'
    });
    window.dataLayer.push({
      event: 'trackEvent',
      eventCategory: 'Publicador',
      eventAction: 'Iniciar Publicacion',
      eventLabel: 'CTA Header Publicar gratis',
      userType
    });
  };

  useEffect(() => {
    setColor(reputationLevelState.levelValue);
  }, [reputationLevelState]);

  useEffect(() => setTextPublish(t('panel.header.btn.publish'), setTextBuy(t('panel.header.btn.buy'))), [
    t,
    setTextPublish,
    setTextBuy
  ]);

  const node = useRef();
  useOutsideClick(
    node,
    () => {
      if (showDropdown) {
        setShowDropdown(!showDropdown);
      } else {
        setShowDropdown(showDropdown);
      }
    },
    'pubid'
  );

  const { showOwner } = useContactOwner();
  const BorderAvatar = ({ number, borderColor }) => (
    <>
      <circle className='secondaryBorder' cx='30' cy='30' r='20' />
      <circle className='externalBorder' r='18' cx='30' cy='30' />

      {number && (
        <rect x='39' y='39' width='12' height='12' rx='2' ry='2' fill={borderColor} stroke='white' strokeWidth='1' />
      )}
      {number && (
        <text x='45' y='48' textAnchor='middle' fontSize='10' fontWeight='bold' fill='white'>
          {number}
        </text>
      )}
    </>
  );

  const nickNameUser = name ? name.toUpperCase().split('')[0] : email && email.toUpperCase().split('')[0];

  return (
    <>
      <MenuRightContainer>
        {showOwner && <ContactOwner />}
        {withHeaderButtons && (
          <>
            {canBuyPlans && !userIsParticular && (
              <Button
                buttonType='noBorderButton'
                dataPanel='headerDesktop_btnBuy'
                linkRef={buyPlansUrl}
                onClick={handleClickBuy}
                text={textBuy}
                header
              />
            )}
            {showModalIntegrator && (
              <Modal>
                <ModalBase
                  openModal={showModalIntegrator}
                  onClose={() => setShowModalIntegrator(false)}
                  width='400px'
                  height='400px'
                >
                  <IntegratorModal
                    linkToEdit={getLinkToPublish(userIsParticular, bumexFeatures)}
                    onClose={() => setShowModalIntegrator(false)}
                    toPost
                  />
                </ModalBase>
              </Modal>
            )}
            {toggleModal && modal === 'timer' && (
              <ModalWithTimer time={redirectPublisher ? '6000' : null} breakpoint={breakpoint}>
                <RedirectMessage
                  isPublisherMasked={isPublisherMasked}
                  portalDestiny={siteId}
                  userPublisherId={publisherId}
                  handleAction={setRedirectPublisher}
                />
              </ModalWithTimer>
            )}
            {bumexFeatures.get(bumexFeaturesKeys.FEEDBACK_NOTIFICATION) && (
              <ThemeProvider theme={theme}>
                <ModalProviderMonorepo>
                  <NotificationButton siteId={siteId} isLogged breakpoint='desktop' isMobile={false} />
                </ModalProviderMonorepo>
              </ThemeProvider>
            )}
            <Button
              buttonType='solidButton'
              dataPanel='headerDesktop_btnPublish'
              linkRef={getLinkToPublish(userIsParticular, bumexFeatures)}
              onClick={handleClick}
              text={textPublish}
              header
              hrefDisabled={isIntegrator || isPublisherMasked}
              idTourReputationLevel='reputation-level-button'
            />
          </>
        )}
        <MenuUserWrapper
          onMouseEnter={() => setShowDropdown(true)}
          // onMouseLeave={() => setShowDropdown(false)}
          ref={node}
          data-panel='headerDesktop_dropDown'
          data-tour-panel='reputation-level'
        >
          <div className='userNameContainer'>
            <AvatarImg
              active={showDropdown}
              borderColor={getColorByNumber(color)}
              Secondary={getBackgroundByNumber(color)}
              activeBorderPrimary={!isPortalWithNewReputation}
            >
              <BorderAvatar number={reputationLevelState.levelValue} borderColor={getColorByNumber(color)} />
            </AvatarImg>
            <div className='userName'>{nickNameUser}</div>
          </div>
          <IconsContainer>
            <span className='arrow-down'>
              <Icon
                name={showDropdown ? 'chevronUp' : 'chevronDown'}
                width={17}
                height={19}
                viewBox='0 0 16 18'
                colorFill='dark'
              />
            </span>
          </IconsContainer>
        </MenuUserWrapper>
      </MenuRightContainer>
      <UserDropdown
        user={user}
        isPortalWithNewReputation={isPortalWithNewReputation}
        reputationLevelState={reputationLevelState}
        showDropdown={showDropdown}
        close={() => setShowDropdown(!showDropdown)}
      />
    </>
  );
}

HeaderMenuRight.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string
  }).isRequired
};
export { HeaderMenuRight };
